import React from 'react';
import { useParams } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';
import { schoolOrderingLink } from '../utils/schoolLinks';

// Admin-facing how-to guide, covering a full lunch order day start to
// finish: opening an order date, the optional teacher list, watching
// orders come in, printing, and archiving once a date has passed. Scoped
// entirely under .help-guide so it can carry its own type/color choices
// without leaking into the rest of the admin UI (which otherwise has no
// shared design-token system to hook into — see index.css).
//
// Content mirrors the standalone tutorial published as a Claude artifact
// during development; keep the two in sync if either changes materially.
export default function Help() {
  const { schoolId } = useParams();
  const { schools } = useSchool();
  const orderingLink = schoolOrderingLink(schoolId, schools.map(s => s.id));

  return (
    <div className="help-guide">
      <style>{`
        .help-guide {
          --hg-paper-raised: #f6f4ee;
          --hg-ink: #20211d;
          --hg-ink-soft: #4a4740;
          --hg-meta: #8a8578;
          --hg-rule: #ddd8cb;
          --hg-accent: #c4622d;
          --hg-accent-soft: #f2ddc9;
          --hg-accent-ink: #8a4321;
          --hg-sage: #4f7a3d;
          --hg-sage-soft: #e2e8da;
          --hg-tag-bg: #eeeae0;
          --hg-tag-border: #d9d3c2;

          max-width: 720px;
          color: var(--hg-ink);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }

        .help-guide .hg-mono {
          font-family: 'SF Mono', ui-monospace, 'Cascadia Code', Consolas, monospace;
        }

        .help-guide .hg-masthead {
          padding-bottom: 1.5rem;
          margin-bottom: 2.5rem;
          border-bottom: 3px solid var(--hg-ink);
        }

        .help-guide .hg-kicker {
          font-style: italic;
          font-size: 0.9rem;
          color: var(--hg-accent-ink);
          margin: 0 0 0.5rem;
        }

        .help-guide h1.hg-title {
          font-size: clamp(1.7rem, 4vw, 2.15rem);
          line-height: 1.15;
          margin: 0 0 0.6rem;
        }

        .help-guide .hg-dek {
          font-size: 1.02rem;
          color: var(--hg-ink-soft);
          max-width: 56ch;
          margin: 0;
        }

        .help-guide .hg-toc {
          margin: 1.75rem 0 0;
          padding: 1.1rem 1.3rem;
          background: var(--hg-paper-raised);
          border: 1px solid var(--hg-rule);
          border-radius: 4px;
        }

        .help-guide .hg-toc-label {
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--hg-meta);
          margin: 0 0 0.65rem;
        }

        .help-guide .hg-toc ol {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          gap: 0.35rem;
        }

        .help-guide .hg-toc a {
          display: flex;
          gap: 0.6rem;
          align-items: baseline;
          text-decoration: none;
          color: var(--hg-ink);
          font-size: 0.92rem;
        }

        .help-guide .hg-toc a:hover { color: var(--hg-accent-ink); }

        .help-guide .hg-toc-num {
          color: var(--hg-meta);
          font-size: 0.82rem;
          min-width: 1.4em;
        }

        .help-guide .hg-toc .hg-opt-flag {
          font-size: 0.7rem;
          color: var(--hg-sage);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .help-guide .hg-spine {
          position: relative;
          margin-top: 3rem;
          padding-left: 3.4rem;
        }

        .help-guide .hg-spine::before {
          content: "";
          position: absolute;
          left: 1.1rem;
          top: 0.4rem;
          bottom: 0.4rem;
          width: 2px;
          background: var(--hg-rule);
        }

        .help-guide section.hg-step {
          position: relative;
          padding-bottom: 2.75rem;
          scroll-margin-top: 1rem;
        }

        .help-guide section.hg-step:last-of-type { padding-bottom: 0; }

        .help-guide .hg-step-num {
          position: absolute;
          left: -3.4rem;
          top: 0.05rem;
          width: 2.15rem;
          height: 2.15rem;
          border-radius: 50%;
          background: #fff;
          border: 2px solid var(--hg-accent);
          color: var(--hg-accent-ink);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .help-guide .hg-step-num.hg-optional {
          border-color: var(--hg-sage);
          color: var(--hg-sage);
          border-style: dashed;
        }

        .help-guide .hg-step-head {
          display: flex;
          align-items: baseline;
          gap: 0.6rem;
          flex-wrap: wrap;
          margin-bottom: 0.3rem;
        }

        .help-guide h2.hg-step-title {
          font-size: 1.3rem;
          margin: 0;
        }

        .help-guide .hg-badge-optional {
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--hg-sage);
          background: var(--hg-sage-soft);
          border: 1px solid #cddabf;
          border-radius: 3px;
          padding: 0.15rem 0.45rem;
        }

        .help-guide .hg-step-where {
          font-size: 0.78rem;
          color: var(--hg-meta);
          margin: 0 0 1rem;
        }

        .help-guide .hg-step-where .hg-sep { color: var(--hg-rule); margin: 0 0.4em; }

        .help-guide .hg-step p {
          margin: 0 0 0.9rem;
          max-width: 60ch;
        }
        .help-guide .hg-step p:last-child { margin-bottom: 0; }

        .help-guide .ui {
          font-family: 'SF Mono', ui-monospace, 'Cascadia Code', Consolas, monospace;
          font-size: 0.85em;
          background: var(--hg-tag-bg);
          border: 1px solid var(--hg-tag-border);
          color: var(--hg-ink-soft);
          border-radius: 3px;
          padding: 0.05em 0.4em;
          white-space: nowrap;
        }

        /* .ui doubles as a clickable link where the tagged value is itself
           a URL (e.g. the school's ordering link) — same tag look, plus a
           hover underline so it still reads as interactive. */
        a.ui {
          text-decoration: none;
        }
        a.ui:hover {
          text-decoration: underline;
          color: var(--hg-accent-ink);
        }

        .help-guide .hg-box {
          border-radius: 5px;
          padding: 0.9rem 1.1rem;
          margin: 1rem 0;
          max-width: 58ch;
          font-size: 0.92rem;
        }
        .help-guide .hg-box p { margin: 0 0 0.4rem; }
        .help-guide .hg-box p:last-child { margin-bottom: 0; }
        .help-guide .hg-box-label {
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          font-weight: 600;
          margin-bottom: 0.35rem;
          display: block;
        }

        .help-guide .hg-box-tip {
          background: var(--hg-sage-soft);
          border: 1px solid #cddabf;
        }
        .help-guide .hg-box-tip .hg-box-label { color: var(--hg-sage); }

        .help-guide .hg-box-note {
          background: var(--hg-accent-soft);
          border: 1px solid #e6bd98;
        }
        .help-guide .hg-box-note .hg-box-label { color: var(--hg-accent-ink); }

        .help-guide .hg-box-caution {
          background: var(--hg-paper-raised);
          border: 1px solid var(--hg-rule);
          border-left: 3px solid var(--hg-ink-soft);
        }
        .help-guide .hg-box-caution .hg-box-label { color: var(--hg-ink-soft); }

        .help-guide .hg-field-table-wrap { overflow-x: auto; }
        .help-guide table.hg-field-table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.9rem;
        }
        .help-guide table.hg-field-table th,
        .help-guide table.hg-field-table td {
          text-align: left;
          padding: 0.5rem 0.7rem;
          border-bottom: 1px solid var(--hg-rule);
          vertical-align: top;
        }
        .help-guide table.hg-field-table th {
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--hg-meta);
          font-weight: 600;
          border-bottom: 1px solid var(--hg-ink-soft);
        }
        .help-guide table.hg-field-table td:first-child {
          font-family: 'SF Mono', ui-monospace, 'Cascadia Code', Consolas, monospace;
          font-size: 0.82rem;
          white-space: nowrap;
        }

        .help-guide .hg-badge {
          display: inline-block;
          font-size: 0.76rem;
          font-weight: 600;
          padding: 0.08rem 0.45rem;
          border-radius: 3px;
        }
        .help-guide .hg-badge-green { background: var(--hg-sage-soft); color: var(--hg-sage); }

        .help-guide .hg-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 0.55rem;
          margin: 0.9rem 0 1.1rem;
        }
        .help-guide .hg-legend-chip {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.82rem;
          background: var(--hg-paper-raised);
          border: 1px solid var(--hg-rule);
          border-radius: 20px;
          padding: 0.28rem 0.7rem 0.28rem 0.5rem;
        }
        .help-guide .hg-dot { width: 0.5rem; height: 0.5rem; border-radius: 50%; flex-shrink: 0; }
        .help-guide .hg-dot-green { background: #4f8a3d; }
        .help-guide .hg-dot-orange { background: var(--hg-accent); }
        .help-guide .hg-dot-gray { background: var(--hg-meta); }
        .help-guide .hg-dot-red { background: #b3462c; }

        .help-guide .hg-paths {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.9rem;
          margin: 1rem 0;
        }
        @media (min-width: 560px) {
          .help-guide .hg-paths { grid-template-columns: 1fr 1fr; }
        }
        .help-guide .hg-path-card {
          background: var(--hg-paper-raised);
          border: 1px solid var(--hg-rule);
          border-radius: 5px;
          padding: 0.9rem 1rem;
        }
        .help-guide .hg-path-card h3 {
          font-size: 0.98rem;
          margin: 0 0 0.4rem;
        }
        .help-guide .hg-path-card p { font-size: 0.88rem; margin: 0; }

        .help-guide .hg-colophon {
          margin-top: 3rem;
          padding-top: 1.25rem;
          border-top: 1px solid var(--hg-rule);
          font-size: 0.82rem;
          color: var(--hg-meta);
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
      `}</style>

      <header className="hg-masthead">
        <p className="hg-kicker">A working guide for school &amp; PTO admins</p>
        <h1 className="hg-title">Running a Lunch Order Day</h1>
        <p className="hg-dek">
          Start to finish — from opening a delivery date for orders
          to clearing out the paperwork once everyone's been fed. Teacher lists
          are optional and only matter if your wizard asks people to pick one.
        </p>
      </header>

      <nav className="hg-toc" aria-label="Table of contents">
        <p className="hg-toc-label hg-mono">On this page</p>
        <ol>
          <li><a href="#help-step-1"><span className="hg-toc-num hg-mono">01</span> Open a lunch date for ordering</a></li>
          <li><a href="#help-step-2"><span className="hg-toc-num hg-mono">02</span> Add teachers by grade <span className="hg-opt-flag hg-mono">optional</span></a></li>
          <li><a href="#help-step-3"><span className="hg-toc-num hg-mono">03</span> Advertise your lunch days</a></li>
          <li><a href="#help-step-4"><span className="hg-toc-num hg-mono">04</span> Manage incoming orders</a></li>
          <li><a href="#help-step-5"><span className="hg-toc-num hg-mono">05</span> Close orders</a></li>
          <li><a href="#help-step-6"><span className="hg-toc-num hg-mono">06</span> Print orders</a></li>
          <li><a href="#help-step-7"><span className="hg-toc-num hg-mono">07</span> Print the order summary</a></li>
          <li><a href="#help-step-8"><span className="hg-toc-num hg-mono">08</span> Archive orders once the date has passed</a></li>
        </ol>
      </nav>

      <div className="hg-spine">

        <section className="hg-step" id="help-step-1">
          <div className="hg-step-num hg-mono">1</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Open a lunch date for ordering</h2>
          </div>
          <p className="hg-step-where hg-mono">Order Dates<span className="hg-sep">&rsaquo;</span>Add New Order Date</p>

          <p>
            Creating an order date is the first step to enabling orders.
            You may set an optional title and description, but the most important
            thing to set is the
            <span className="ui">Delivery Date</span> — the actual day
            the sandwiches show up.
          </p>

          <p>
            Setting the delivery date auto-fills two more fields, which you're
            free to adjust:
          </p>

          <div className="hg-field-table-wrap">
            <table className="hg-field-table">
              <thead>
                <tr><th>Field</th><th>Default</th><th>What it controls</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Order Window Open Date</td>
                  <td>30 days before delivery</td>
                  <td>Orders can't be placed before this date.</td>
                </tr>
                <tr>
                  <td>Order Window Close Date / Time</td>
                  <td>7 days before, 5:00&nbsp;PM</td>
                  <td>Ordering shuts off here.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>Once it's live, each order date shows a status at a glance:</p>

          <div className="hg-legend">
            <span className="hg-legend-chip"><span className="hg-dot hg-dot-orange"></span>Not Yet Open</span>
            <span className="hg-legend-chip"><span className="hg-dot hg-dot-green"></span>Orders Open</span>
            <span className="hg-legend-chip"><span className="hg-dot hg-dot-gray"></span>Closed</span>
            <span className="hg-legend-chip"><span className="hg-dot hg-dot-red"></span>Inactive</span>
          </div>

          <div className="hg-box hg-box-tip">
            <span className="hg-box-label">Closing early, or reopening</span>
            <p>
              Need to cut off orders ahead of schedule? Open the date and use{' '}
              <span className="ui">Close Orders Now</span>.
            </p>
            <p>
              Changed your mind, or a date closed before enough people
              ordered? There's no separate "reopen" button — open{' '}
              <span className="ui">Edit</span> on that date and move the{' '}
              <span className="ui">Order Window Close Date</span> forward to
              whenever you want ordering to stop instead.
            </p>
          </div>

          <div className="hg-box hg-box-caution">
            <span className="hg-box-label">Can't delete a date with orders on it</span>
            <p>
              <span className="ui">Delete</span> is disabled when there are orders attached to the
              date. If you want to delete the order date, delete the orders associated with that date first.
            </p>
          </div>
        </section>

        <section className="hg-step" id="help-step-2">
          <div className="hg-step-num hg-optional hg-mono">2</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Add teachers by grade</h2>
            <span className="hg-badge-optional hg-mono">Optional</span>
          </div>
          <p className="hg-step-where hg-mono">Teachers<span className="hg-sep">&rsaquo;</span>Add New Teacher</p>

          <p>
            If you would like sandwiches to be organized by teacher (e.g. elementary schools),
            you may add a list of teachers to the app so that orders will include this information.
          </p>

          <div className="hg-box hg-box-tip">
            <span className="hg-box-label">Someone left</span>
            <p>
              When a teacher should no longer appear in the list, inactivate them to
              prevent their name from showing up at order entry time.
            </p>
          </div>
        </section>

        <section className="hg-step" id="help-step-3">
          <div className="hg-step-num hg-mono">3</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Advertise your lunch days</h2>
          </div>

          <p>
            Publish your custom link for individuals to order. Typically these are published
            in newsletters and printed flyers. For ease of scanning, you may also create QR codes
            that allow easily opening the website.
          </p>

          <p>
            Your custom link is{' '}
            <a href={orderingLink} className="ui">{orderingLink}</a>
          </p>
        </section>

        <section className="hg-step" id="help-step-4">
          <div className="hg-step-num hg-mono">4</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Manage incoming orders</h2>
          </div>
          <p className="hg-step-where hg-mono">View Orders<span className="hg-sep">&rsaquo;</span>your default landing page</p>

          <p>
            As orders come in, they will be displayed in the "View Orders" section of this website.
            While this records the order, it does not correlate the fact of payment with the order.
            Admins must manually mark orders as paid, either through venmo, cash, or check. This
            is best done as orders come in.
          </p>

          <p>
            If orders are not marked as paid, they will not be printed in later steps. Admins may
            choose to reach out to those who have submitted orders but not paid.
          </p>

          <div className="hg-box hg-box-note">
            <span className="hg-box-label">Confirmation emails</span>
            <p>
              If an email is provided at time of order, a confirmation is sent with the details of their sandwich.
            </p>
          </div>
        </section>

        <section className="hg-step" id="help-step-5">
          <div className="hg-step-num hg-mono">5</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Close orders</h2>
          </div>
          <p className="hg-step-where hg-mono">Order Dates<span className="hg-sep">&rsaquo;</span>Close Orders Now</p>

          <p>
            Orders will close automatically at the intended time. If there is a desire to close early or later, use the Order Dates UI
            to change as needed. If orders are closed and you want to reopen, simply edit the Order Date and extend the Order Window Close Date.
          </p>
        </section>

        <section className="hg-step" id="help-step-6">
          <div className="hg-step-num hg-mono">6</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Print orders</h2>
          </div>
          <p className="hg-step-where hg-mono">Print Preview<span className="hg-sep">&rsaquo;</span>Print Orders</p>

          <p>
            Orders are printed by grade. Change to landscape mode to fit up to 6 orders per page.
            Don't forget to disable two-sided printing. Only
            <span className="hg-badge hg-badge-green">Paid</span> orders are printed. The rest will
            be left behind.
          </p>

          <p>
            After printing, Lunchday confirms that the orders printed correctly. When confirmed,
            the printed orders will become marked as printed, and won't print again. If confirmed
            properly, you won't get duplicate orders printed.
          </p>
        </section>

        <section className="hg-step" id="help-step-7">
          <div className="hg-step-num hg-mono">7</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Print the order summary</h2>
          </div>
          <p className="hg-step-where hg-mono">Order Summary<span className="hg-sep">&rsaquo;</span>Print Summary</p>

          <p>
            In addition to the orders themselves, a summary is provided to Subway for each grade to ensure
            all orders are correctly captured.
          </p>
        </section>

        <section className="hg-step" id="help-step-8">
          <div className="hg-step-num hg-mono">8</div>
          <div className="hg-step-head">
            <h2 className="hg-step-title">Archive orders once the date has passed</h2>
          </div>
          <p className="hg-step-where hg-mono">Orders Management<span className="hg-sep">&rsaquo;</span>Delete Paid Orders</p>

          <p>
            Once delivery's done, clear the paid orders out of your active
            list so the next lunch day starts with a clean slate.
          </p>

          <p>
            In <span className="ui">Orders Management</span>,{' '}
            <span className="ui">Delete All Paid Orders</span> clears
            every paid and printed order for the school in one pass.
            This should only be done once orders have been delivered.
          </p>

          <p>
            Unpaid and unprinted orders are not cleared.
          </p>
        </section>
      </div>

      <div className="hg-colophon">
        <span>Lunchday — group lunch ordering for schools &amp; nonprofits</span>
        <span>Questions: hello@lunchday.app</span>
      </div>
    </div>
  );
}
